import { CartItemType } from "../../services/redux/types/CartLoyaltyV2Types";
import CartItem from "../CartItem";
import { ItemDetailsType, ItemType, SubItemType } from "../types";

/** Raw fields for building a cart line (V2 LoyaltyLine or legacy CartItem). */
export type AddLineInput = {
  note: string;
  count: number;
  totalPrice: number;
  subItemDetails: SubItemType;
  itemDetails: ItemDetailsType;
  version: number;
  isFreeItem?: boolean;
  discountedItemPercentage?: number;
  customDeliveryTime?: number;
  excludeFromOffers?: boolean;
  excludeFromPointsPayment?: boolean;
  priceWithoutOffer?: number;
  isDiscountedItem?: boolean;
  lineId?: string;
  /** Legacy-only — ignored on V2 mutation path */
  isBuyItem?: boolean;
  isBundleItem?: boolean;
  isOfferItem?: boolean;
  useLoyaltyService?: boolean;
};

export type RemoveLineInput =
  | { lineId: string; useLoyaltyService?: boolean }
  | { itemIndex: number | number[]; useLoyaltyService?: boolean };

export function isCartItemPayload(payload: unknown): payload is CartItem {
  return (
    payload instanceof CartItem ||
    (typeof payload === "object" &&
      payload !== null &&
      typeof (payload as CartItem).toJson === "function")
  );
}

export function cartItemTypeToAddLineInput(item: CartItemType): AddLineInput {
  return {
    note: item.note,
    count: item.count,
    totalPrice: item.totalPrice,
    subItemDetails: item.subItemDetails,
    itemDetails: item.itemDetails,
    version: item.version,
    isFreeItem: item.isFreeItem,
    discountedItemPercentage: item.discountedItemPercentage,
    customDeliveryTime: item.customDeliveryTime,
    excludeFromOffers: item.excludeFromOffers,
    excludeFromPointsPayment: item.excludeFromPointsPayment,
    priceWithoutOffer: item.priceWithoutOffer,
    isDiscountedItem: item.isDiscountedItem,
    isBuyItem: item.isBuyItem,
    isBundleItem: item.isBundleItem,
    isOfferItem: item.isOfferItem,
    lineId: item.lineId,
  };
}

export function cartItemToAddLineInput(
  payload: CartItem | CartItemType | AddLineInput
): AddLineInput {
  if (isCartItemPayload(payload)) {
    return cartItemTypeToAddLineInput(payload.toJson());
  }

  if (
    typeof payload === "object" &&
    payload !== null &&
    "subItemDetails" in payload &&
    "itemDetails" in payload
  ) {
    return cartItemTypeToAddLineInput(payload as CartItemType);
  }

  return payload as AddLineInput;
}

export function addLineInputToCartItemType(input: AddLineInput): CartItemType {
  return {
    lineId: input.lineId,
    subItemDetails: input.subItemDetails,
    itemDetails: input.itemDetails,
    note: input.note,
    count: input.count,
    totalPrice: input.totalPrice,
    version: input.version,
    isFreeItem: input.isFreeItem,
    discountedItemPercentage: input.discountedItemPercentage,
    customDeliveryTime: input.customDeliveryTime,
    excludeFromOffers: input.excludeFromOffers,
    excludeFromPointsPayment: input.excludeFromPointsPayment,
    isBuyItem: input.isBuyItem,
    isBundleItem: input.isBundleItem,
    isOfferItem: input.isOfferItem,
    isDiscountedItem: input.isDiscountedItem,
    priceWithoutOffer: input.priceWithoutOffer,
  };
}

export function buildCategoryAddLineInput(
  note: string,
  count: number,
  totalPrice: number,
  subItemDetails: SubItemType,
  item: ItemType,
  options?: {
    version?: number;
    isFreeItem?: boolean;
    discountedItemPercentage?: number;
  }
): AddLineInput {
  const subItem = { ...subItemDetails };
  if (!subItem.allAddonGroups) {
    subItem.allAddonGroups = [];
  }

  return {
    note,
    count,
    totalPrice,
    subItemDetails: subItem,
    itemDetails: {
      id: item.id!,
      img_url: item.img_url!,
      item_name: item.item_name!,
      originalPrice: subItem.price,
      customVatPercentage: item.customVatPercentage,
      uuid: item.uuid,
    },
    version: options?.version ?? item.version!,
    customDeliveryTime: item.customDeliveryTime ?? 0,
    excludeFromOffers: item.excludeFromOffers,
    isFreeItem: options?.isFreeItem,
    discountedItemPercentage: options?.discountedItemPercentage,
  };
}

export function normalizeAddPayload(
  payload: CartItem | AddLineInput
): AddLineInput {
  if (isCartItemPayload(payload)) {
    return {
      ...cartItemToAddLineInput(payload),
      useLoyaltyService: (payload as AddLineInput).useLoyaltyService,
    };
  }

  return payload as AddLineInput;
}
